import {
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button, Space, Table, Tooltip, Input } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { SearchOutlined } from '@ant-design/icons';
import useBeerAddedStock from "../../hooks/useBeerAddedStock";
import useMemberProfilesList from "../../hooks/useMemberProfilesList";
import Highlighter from 'react-highlight-words';
import jsPDF from "jspdf";
import "jspdf-autotable";

function BeerAddedDataTable(props) {
  const { getBeerAddedStockCount } = props;
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);

  const memberProfileList = useMemberProfilesList("");
  const beerAddedStockList = useBeerAddedStock("");

  useEffect(() => {
    const tempAddedStock = []
    beerAddedStockList?.map((beer_added_stock_data) => {
      memberProfileList?.map((member_profile_data) => {

        if (beer_added_stock_data.member_id === member_profile_data.member_id) {
          const tempAddedQuantity = []
          beer_added_stock_data.added_quantity.map((added_quantity_data) => {
            tempAddedQuantity.push({
              "quantity_data": `${added_quantity_data.volume}ml - ${added_quantity_data.quantity}`
            })

            return null
          })
          tempAddedStock.push({
            "key": beer_added_stock_data.stock_number,
            "stock_number": beer_added_stock_data.stock_number,
            "purchased_person": `${member_profile_data.first_name} ${member_profile_data.last_name}`,
            "purchase_date": beer_added_stock_data.purchase_date,
            "quantity_description": tempAddedQuantity,
            "invoice": beer_added_stock_data.invoice
          })
        }

        return null
      })

      return null
    })

    setData(tempAddedStock)
    getBeerAddedStockCount(beerAddedStockList.at(-1)?.stock_number || 0)
  }, [beerAddedStockList, getBeerAddedStockCount, memberProfileList])

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const exportAddedStockDataPDF = (record) => {
    const unit = "pt";
    const size = "A4"; // Use A1, A2, A3 or A4
    const orientation = "portrait"; // portrait or landscape
    const marginLeft = 40;
    const doc = new jsPDF(orientation, unit, size);
    doc.setFontSize(15);

    const title = "Colombo Hash";
    const subtitle = "Added Stock Data"; // New subtitle text
    const headers = [[
      "Stock Number",
      "Purchased Person",
      "Purchase Date",
      "Quantity Description"
    ]];

    const tableData = [record].map((elt) => [
      elt.stock_number,
      elt.purchased_person,
      elt.purchase_date,
      elt.quantity_description.map((quantity_description) => quantity_description.quantity_data).join(),
    ]);

    let content = {
      startY: 65,
      head: headers,
      body: tableData,
    };

    // Add title and new subtitle
    doc.text(title, marginLeft, 30);
    doc.setFontSize(12); // Reduce font size for the subtitle
    doc.text(subtitle, marginLeft, 50); // Add the new subtitle below the title

    // Get the current date and time
    const now = new Date();
    const generatedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    // Add generated date and time in the bottom right corner
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(10); // Adjust font size for the generated date and time
    doc.text(generatedDateTime, pageWidth - marginLeft, pageHeight - 30, {
      align: "right",
    });

    doc.autoTable(content);
    doc.save(`stock-number${record.stock_number}-added-stock.pdf`);
  };

  const handleDownloadInvoice = (record) => {
    // Get the invoice URL from the record object
    const invoiceUrl = record.invoice;
    window.open(invoiceUrl, '_blank');
  };

  const columns = [
    {
      title: "Stock Number",
      dataIndex: "stock_number",
      sorter: (a, b) => a.stock_number - b.stock_number,
      sortOrder: 'descend',
      ...getColumnSearchProps('stock_number'),
    },
    {
      title: "Purchased Person",
      dataIndex: "purchased_person",
      ...getColumnSearchProps('purchased_person'),
    },
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      ...getColumnSearchProps('purchase_date'),
    },
    {
      title: "Quantity Description",
      dataIndex: "quantity_description",
      render: (quantity_description) => quantity_description.map((quantity_description) => quantity_description.quantity_data).join(),
    },
    {
      title: "Action",
      render: (text, record) => (
        <Space>
          <Tooltip placement="bottom" title="Download Invoice">
            <Button
              className="edit_button"
              shape="circle"
              icon={<FileTextOutlined />}
              onClick={() => exportAddedStockDataPDF(record)}
            />
          </Tooltip>
          {record.invoice ? <Tooltip placement="bottom" title="Download Payment Slip">
            <Button
              className="edit_button"
              shape="circle"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadInvoice(record)}
            />
          </Tooltip> : <></>}
        </Space>
      ),
      fixed: "right",
      align: 'left',
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        bordered
        pagination
        scroll={{ x: 1500 }}
      />
    </>
  );
}

export default BeerAddedDataTable;
